import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, Eye, EyeOff, ShieldCheck, Fingerprint } from "lucide-react";
import {
  QUESTIONS, PHASE_LABELS, OTHER, computeDiagnostic, buildAnswersForApi,
  q12Options, shouldSkipQ12, reassuranceFor,
} from "@/data/quiz";
import { api, getSessionId, saveAnswers, loadAnswers, clearAnswers } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Counter, Typewriter, Reveal } from "@/components/anim";
import { ReviewTicker } from "@/components/ReviewTicker";
import { trackWhop } from "@/lib/whop";
import { clarityEvent } from "@/lib/clarity";
import { useAuth } from "@/context/AuthContext";

const QById = Object.fromEntries(QUESTIONS.map((q) => [q.id, q]));
const INTRO_SCREENS = [
  { icon: "sparkle", event: "intro_1", label: "BIENVENUE", title: "Bienvenue dans Idevorai.", text: "Ton business sur-mesure, ton plan jour par jour et ton coach IA. Voilà comment on t'amène à ton objectif.", cta: "Suivant" },
  { icon: "bulb", event: "intro_2", label: "TON BUSINESS", title: "Une idée faite pour toi.", text: "Choisie selon ton budget, ton temps et tes envies. Pas une idée au hasard.", cta: "Suivant" },
  { icon: "phone", event: "intro_3", label: "TON PLAN", title: "Chaque matin, ta mission.", text: "Plus besoin de savoir par où commencer : tu ouvres l'app, tu fais ta mission du jour, tu avances.", cta: "Suivant" },
  { icon: "podium", event: "intro_4", label: "TON OBJECTIF", title: "Tes premiers euros.", text: "Un revenu à toi, construit étape par étape. À toi de jouer.", cta: "Continuer" },
];
const FIRST5 = ["q1", "q2", "q3", "q4", "q5"];
const OTHER_IDS = ["q7", "q8", "q9", "q10", "q11", "q12", "q13", "q14", "q15", "q16", "q17", "q18", "q19", "q20"];
// Question counter: first 5 + objective + type + the other questions.
const QUESTION_TYPES = new Set(["question", "objective", "typebiz"]);

function buildScreens() {
  const s = [];
  FIRST5.forEach((id) => s.push({ type: "question", q: QById[id] }));
  s.push({ type: "objective" });      // 6e position — objectif à 3 mois (curseur)
  s.push({ type: "socialproof" });    // preuve sociale + avis résultats (avant la projection)
  s.push({ type: "projloading" });
  s.push({ type: "projection" });
  INTRO_SCREENS.forEach((d) => s.push({ type: "intro", data: d }));
  s.push({ type: "cap" });
  s.push({ type: "typebiz" });        // Tu préfères lancer quoi ? (écrit dans q6)
  s.push({ type: "words" });          // Tes mots
  s.push({ type: "prenom" });         // Ton prénom
  s.push({ type: "presquefini" });
  OTHER_IDS.forEach((id) => s.push({ type: "question", q: QById[id] }));
  s.push({ type: "debloques" });
  s.push({ type: "account" });
  s.push({ type: "garantie" });
  s.push({ type: "pacte" });
  s.push({ type: "loading" });
  return s;
}
const TOTAL = FIRST5.length + 1 + 1 + OTHER_IDS.length; // questions comptées

const EASE = [0.22, 1, 0.36, 1];

export default function Quiz() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { isAuthed } = useAuth();
  const screens = useMemo(buildScreens, []);
  const [index, setIndex] = useState(() => {
    const saved = parseInt(sessionStorage.getItem("idealab_qidx") || "0", 10);
    return Number.isFinite(saved) ? Math.min(Math.max(saved, 0), screens.length - 1) : 0;
  });
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState(() => loadAnswers());

  const screen = screens[index];

  useEffect(() => { saveAnswers(answers); }, [answers]);
  useEffect(() => { sessionStorage.setItem("idealab_qidx", String(index)); }, [index]);

  const skipScreen = (s) =>
    (s.type === "question" && s.q.id === "q12" && shouldSkipQ12(answers)) ||
    (s.type === "account" && isAuthed);

  // Auto-skip the account gate once authenticated (also after Google redirect).
  useEffect(() => {
    if (screen && skipScreen(screen)) go(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, isAuthed]);

  const go = (delta) => {
    setDir(delta);
    setIndex((i) => {
      let next = i + delta;
      while (next > 0 && next < screens.length - 1 && skipScreen(screens[next])) next += delta;
      return Math.min(Math.max(next, 0), screens.length - 1);
    });
    window.scrollTo({ top: 0 });
  };

  const goBackHome = () => { if (index === 0) navigate("/"); else go(-1); };

  const selectChoice = (q, value) => {
    setAnswers((a) => ({ ...a, [q.id]: value }));
    if (value !== OTHER) setTimeout(() => go(1), 900);
  };

  const toggleMulti = (q, value) => {
    setAnswers((a) => {
      const arr = Array.isArray(a[q.id]) ? [...a[q.id]] : [];
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(value);
      return { ...a, [q.id]: arr };
    });
  };

  const questionNumber = QUESTION_TYPES.has(screen.type)
    ? screens.slice(0, index + 1).filter((s) => QUESTION_TYPES.has(s.type)).length
    : null;
  const prenom = (answers.prenom || "").trim();
  useEffect(() => { if (questionNumber) clarityEvent(`question_${questionNumber}`); }, [questionNumber]);
  useEffect(() => {
    if (questionNumber && screen.type === "question") {
      api.post("/quiz/progress", { session_id: getSessionId(), qnum: questionNumber, qid: screen.q.id, question: screen.q.question }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionNumber]);
  const progress = Math.min(100, Math.round(((index + 1) / (screens.length - 1)) * 100));

  const variants = reduce
    ? { enter: { opacity: 0 }, center: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        enter: (d) => ({ opacity: 0, x: d > 0 ? 20 : -20 }),
        center: { opacity: 1, x: 0 },
        exit: (d) => ({ opacity: 0, x: d > 0 ? -20 : 20 }),
      };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="quiz-header">
        <div className="container quiz-header-inner">
          <Logo onClick={() => navigate("/")} />
          {questionNumber && (
            <span className="quiz-counter mono" data-testid="quiz-question-count">QUESTION {questionNumber} / {TOTAL}</span>
          )}
        </div>
        <div className="progress-track" role="progressbar" aria-label="Progression du questionnaire" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} data-testid="quiz-progress">
          <motion.div className="progress-fill" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
        </div>
      </header>
      <main className="container quiz-layout">
        <aside className="quiz-rail">
          <span className="eyebrow">TON BUSINESS<br className="hidden md:block" /> COMMENCE ICI.</span>
          <p>Ta situation.<br />Tes ambitions.<br />Ton prochain chapitre.</p>
        </aside>
        <div className="quiz-content">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={index}
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: reduce ? 0.2 : 0.35, ease: EASE }}
            >
              {screen.type === "question" && (
                <QuestionScreen
                  q={screen.q}
                  num={questionNumber}
                  answers={answers}
                  setAnswers={setAnswers}
                  selectChoice={selectChoice}
                  toggleMulti={toggleMulti}
                  onNext={() => go(1)}
                  onBack={goBackHome}
                />
              )}
              {screen.type === "objective" && <ObjectiveScreen answers={answers} setAnswers={setAnswers} onNext={() => go(1)} onBack={goBackHome} />}
              {screen.type === "socialproof" && <SocialProofScreen onNext={() => go(1)} />}
              {screen.type === "projloading" && <ProjectionLoading onNext={() => go(1)} />}
              {screen.type === "projection" && <ProjectionScreen answers={answers} onNext={() => go(1)} />}
              {screen.type === "intro" && <IntroScreen data={screen.data} onNext={() => go(1)} />}
              {screen.type === "cap" && <CapScreen answers={answers} onNext={() => go(1)} />}
              {screen.type === "typebiz" && <TypeBusinessScreen answers={answers} setAnswers={setAnswers} onNext={() => go(1)} />}
              {screen.type === "words" && <WordsScreen answers={answers} setAnswers={setAnswers} onNext={() => go(1)} />}
              {screen.type === "prenom" && <PrenomScreen answers={answers} setAnswers={setAnswers} onNext={() => go(1)} />}
              {screen.type === "presquefini" && <PresqueFiniScreen prenom={prenom} onNext={() => go(1)} />}
              {screen.type === "debloques" && <DebloquesScreen onNext={() => go(1)} />}
              {screen.type === "garantie" && <GarantieScreen onNext={() => go(1)} />}
              {screen.type === "account" && <AccountScreen />}
              {screen.type === "pacte" && <PacteScreen onDone={() => go(1)} />}
              {screen.type === "loading" && <LoadingScreen answers={answers} navigate={navigate} />}
            </motion.div>

          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

function QuestionScreen({ q, num, answers, setAnswers, selectChoice, toggleMulti, onNext, onBack }) {
  const reduce = useReducedMotion();
  const value = answers[q.id];
  const otherText = answers[q.id + "_other"] || "";
  const options = q.dynamicFrom ? q12Options(answers) : q.options;

  const otherVisible =
    (q.type === "choice" && value === OTHER) ||
    (q.type === "multi" && Array.isArray(value) && value.includes(OTHER));

  const setOther = (val) => setAnswers((a) => ({ ...a, [q.id + "_other"]: val }));

  // Next button validity
  let showNext = false;
  let nextEnabled = false;
  if (q.type === "slider") { showNext = true; nextEnabled = !!value; }
  else if (q.type === "multi") {
    showNext = true;
    const arr = Array.isArray(value) ? value : [];
    const eff = arr.filter((x) => x !== OTHER);
    nextEnabled = eff.length > 0 || (arr.includes(OTHER) && otherText.trim().length > 0);
  } else if (q.type === "choice" && value === OTHER) {
    showNext = true;
    nextEnabled = otherText.trim().length > 0;
  }

  const optAnim = (i) => (reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
    : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35, delay: i * 0.05, ease: EASE } });

  const isSel = (opt) => (q.type === "multi" ? (Array.isArray(value) && value.includes(opt)) : value === opt);
  const onOptClick = (opt) => (q.type === "multi" ? toggleMulti(q, opt) : selectChoice(q, opt));

  return (
    <div>
      <button onClick={onBack} data-testid="quiz-back" className="quiz-back mono">← Retour</button>
      <p className="eyebrow mt-3" data-testid="quiz-step-label">{String(num).padStart(2, "0")} — {PHASE_LABELS[q.phase]}</p>
      <h1 className="quiz-question" data-testid="quiz-question">{q.question}</h1>

      {(q.type === "choice" || q.type === "multi") && (
        <div className="option-list">
          {options.map((opt, i) => (
            <div key={opt} className="option-item">
              <OptionCard opt={opt} index={i} multi={q.type === "multi"} selected={isSel(opt)} onClick={() => onOptClick(opt)} anim={optAnim(i)} testid={`opt-${q.id}-${i}`} />
              {isSel(opt) && reassuranceFor(q, opt) && <ReassureLine text={reassuranceFor(q, opt)} testid={`reassure-${q.id}-${i}`} />}
            </div>
          ))}
          {q.hasOther && (
            <div className="option-item">
              <OptionCard opt={OTHER} index={options.length} multi={q.type === "multi"} selected={isSel(OTHER)} onClick={() => onOptClick(OTHER)} anim={optAnim(options.length)} testid={`opt-${q.id}-other`} />
              {isSel(OTHER) && reassuranceFor(q, OTHER) && <ReassureLine text={reassuranceFor(q, OTHER)} testid={`reassure-${q.id}-other`} />}
            </div>
          )}
          {otherVisible && (
            <input
              autoFocus
              aria-label="Autre réponse"
              data-testid={`${q.id}-other-input`}
              value={otherText}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Écris ta réponse ici"
              className="field field-other"
            />
          )}
        </div>
      )}

      {q.type === "scale" && (
        <>
          <div className="scale-options">
            {q.options.map((opt, i) => (
              <motion.button key={opt} {...optAnim(i)} onClick={() => selectChoice(q, opt)} data-testid={`opt-${q.id}-${i}`} aria-pressed={value === opt} className={`option scale-option ${value === opt ? "option-selected" : ""}`}>
                <span className="mono">0{i + 1}</span><span>{opt}</span>
              </motion.button>
            ))}
          </div>
          {value && reassuranceFor(q, value) && <ReassureLine text={reassuranceFor(q, value)} testid={`reassure-${q.id}`} />}
        </>
      )}

      {q.type === "slider" && (
        <>
          <SliderQuestion q={q} value={value} setAnswers={setAnswers} />
          {value && <ReassureLine text={reassuranceFor(q, value)} testid={`reassure-${q.id}`} />}
        </>
      )}

      {showNext && (
        <button onClick={onNext} disabled={!nextEnabled} data-testid="quiz-next" className="btn-primary w-full mt-8">
          {q.id === "q21" ? "Créer mon business" : "Suivant"} <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
}

function ReassureLine({ text, testid }) {
  const reduce = useReducedMotion();
  return (
    <motion.p
      className="reassure"
      data-testid={testid}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
    >
      {text}
    </motion.p>
  );
}

function OptionCard({ opt, index, multi, selected, onClick, anim, testid }) {
  return (
    <motion.button {...anim} onClick={onClick} data-testid={testid} aria-pressed={selected} className={`option ${selected ? "option-selected" : ""}`}>
      <span className="option-index mono" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
      <span className="flex-1">{opt}</span>
      <span className={multi ? "option-indicator" : "option-indicator"} aria-hidden="true">{selected && <Check size={14} />}</span>
    </motion.button>
  );
}

function SliderQuestion({ q, value, setAnswers }) {
  const def = Math.round(((q.min + q.max) / 2) / q.step) * q.step;
  const v = value || def;
  useEffect(() => {
    if (!value) setAnswers((a) => ({ ...a, [q.id]: def }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fillPct = ((v - q.min) / (q.max - q.min)) * 100;
  return (
    <div className="revenue-panel">
      <p className="eyebrow">TON OBJECTIF À 3 MOIS</p>
      <div key={v} className="revenue-value revenue-value--pop" data-testid="revenue-value">{v.toLocaleString("fr-FR")} €</div>
      <div className="revenue-slider-wrap">
        <div className="revenue-track" aria-hidden="true"><div className="revenue-track-fill" style={{ width: `${fillPct}%` }} /></div>
        <input
          type="range"
          aria-label="Objectif de revenus dans trois mois"
          aria-valuetext={`${v.toLocaleString("fr-FR")} euros`}
          min={q.min}
          max={q.max}
          step={q.step}
          value={v}
          data-testid="revenue-slider"
          onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: Number(e.target.value) }))}
          className="revenue-slider"
        />
      </div>
      <div className="flex justify-between text-xs muted mono mt-5">
        <span>{q.min.toLocaleString("fr-FR")}€</span>
        <span>{q.max.toLocaleString("fr-FR")}€</span>
      </div>
    </div>
  );
}

function SocialScreen({ onNext }) {
  return (
    <div className="interstitial" data-testid="social-screen">
      <p className="eyebrow">LE LABO PREND VIE</p>
      <div className="interstitial-number" data-testid="social-user-count">
        +<Counter to={50000} duration={1.4} format={(v) => v.toLocaleString("fr-FR")} />
      </div>
      <Reveal delay={0.4}><h2>T'es pas <em>le premier.</em></h2></Reveal>
      <Reveal delay={0.6}><p>+50 000 utilisateurs nous ont fait confiance et on a créé leur business. Et pas seulement créé : ils ont généré leurs premiers revenus.</p></Reveal>
      <Reveal delay={0.8}><button onClick={onNext} data-testid="social-continue" className="btn-primary mt-10">Continuer <span aria-hidden="true">→</span></button></Reveal>
      <ReviewTicker variant="left" />
    </div>
  );
}

function DiagnosticScreen({ answers, onNext }) {
  const diag = useMemo(() => computeDiagnostic(answers), [answers]);
  const [done, setDone] = useState(false);
  return (
    <div className="interstitial" data-testid="diagnostic-screen">
      <p className="eyebrow">01 — TON DIAGNOSTIC</p>
      <h2>Résultat du <em>diagnostic.</em></h2>
      <div className="diagnostic-panel" data-testid="diagnostic-result">
        <Typewriter text={diag} speed={30} onDone={() => setDone(true)} />
      </div>
      <Reveal delay={done ? 0 : 0.1}><p>Pour finaliser ton plan, aide-moi à mieux comprendre ton projet avec quelques questions courtes.</p></Reveal>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: done ? 1 : 0.4 }}
        onClick={onNext}
        data-testid="diagnostic-continue"
        className="btn-primary mt-8"
      >
        Continuer <span aria-hidden="true">→</span>
      </motion.button>
      <ReviewTicker variant="left" />
    </div>
  );
}

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

function AccountScreen() {
  const { register, login, loginGoogle } = useAuth();
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwValid = password.length >= 8;
  const canSubmit = emailValid && (mode === "login" ? password.length > 0 : pwValid);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true); setError("");
    try {
      if (mode === "signup") { await register(email.trim(), password); trackWhop("complete_registration"); }
      else await login(email.trim(), password);
      // Advancing is handled by the isAuthed effect in Quiz (avoids double-skip).
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Une erreur est survenue, réessaie.");
      setSubmitting(false);
    }
  };

  return (
    <div className="account-screen" data-testid="account-screen">
      <p className="eyebrow">TON COMPTE</p>
      <h2>Crée ton compte pour sauvegarder ta <em>progression.</em></h2>
      <p className="account-sub">Tu retrouveras ton business et ton plan à tout moment, sur n'importe quel appareil.</p>

      <form onSubmit={submit} className="account-form">
        <label className="field-label" htmlFor="acc-email">Email</label>
        <input id="acc-email" type="email" autoComplete="email" data-clarity-mask="true" data-testid="account-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ton@email.com" className="field" />
        <label className="field-label mt-4" htmlFor="acc-pw">Mot de passe</label>
        <div className="pw-wrap">
          <input id="acc-pw" type={showPw ? "text" : "password"} autoComplete={mode === "signup" ? "new-password" : "current-password"} data-clarity-mask="true" data-testid="account-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "signup" ? "8 caractères minimum" : "Ton mot de passe"} className="field" />
          <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password" className="pw-eye" aria-label={showPw ? "Masquer" : "Afficher"}>{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
        </div>
        {error && <p className="error-message" data-testid="account-error">{error}</p>}
        <button type="submit" disabled={!canSubmit || submitting} data-testid="account-submit" className="btn-primary w-full mt-6">
          {submitting ? "..." : mode === "signup" ? "Créer mon compte" : "Se connecter"}
        </button>
      </form>

      <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(""); }} data-testid="account-toggle-mode" className="account-toggle">
        {mode === "signup" ? "J'ai déjà un compte — me connecter" : "Créer un compte à la place"}
      </button>
    </div>
  );
}

function TransitionScreen({ onNext }) {
  return (
    <div className="interstitial" data-testid="phase3-screen">
      <p className="eyebrow">PHASE 3</p>
      <h2>Maintenant, parle-moi de ce que tu sais <em>faire.</em></h2>
      <Reveal delay={0.3}><p>C'est ce qui va rendre ton business unique.</p></Reveal>
      <Reveal delay={0.5}><button onClick={onNext} data-testid="phase3-continue" className="btn-primary mt-8">Continuer <span aria-hidden="true">→</span></button></Reveal>
      <ReviewTicker variant="left" />
    </div>
  );
}

const LOAD_STEPS = [
  { t: 0.00, label: "Analyse de ton profil…" },
  { t: 0.15, label: "Étude de ton budget de départ…" },
  { t: 0.32, label: "Recherche des business les plus rentables…" },
  { t: 0.52, label: "Calcul de ton objectif à 3 mois…" },
  { t: 0.72, label: "Création de ton plan 30 jours…" },
  { t: 0.88, label: "Dernières vérifications…" },
];
const DURATION = 20000;
const FINAL_TAU = 14000; // fluid creep 94%→99% while backend finishes
const RADIUS = 90;
const CIRC = 2 * Math.PI * RADIUS;

function curve(t) {
  // Natural stop-and-go (bursts then short plateaus) toward ~0.94 across the duration.
  const pts = [
    [0.00, 0.00], [0.11, 0.20], [0.17, 0.22],
    [0.33, 0.44], [0.40, 0.46], [0.55, 0.64],
    [0.62, 0.66], [0.80, 0.85], [0.88, 0.87], [1.00, 0.94],
  ];
  for (let i = 1; i < pts.length; i++) {
    if (t <= pts[i][0]) {
      const [t0, p0] = pts[i - 1];
      const [t1, p1] = pts[i];
      const k = (t - t0) / (t1 - t0 || 1);
      return p0 + (p1 - p0) * k;
    }
  }
  return 0.94;
}

function ObjectiveScreen({ answers, setAnswers, onNext, onBack }) {
  const q = QById.q21;
  const value = answers.q21;
  return (
    <div data-testid="objective-screen">
      <button onClick={onBack} data-testid="quiz-back" className="quiz-back mono">← Retour</button>
      <p className="eyebrow mt-3">OBJECTIF — 3 MOIS</p>
      <h1 className="quiz-question">{q.question}</h1>
      <SliderQuestion q={q} value={value} setAnswers={setAnswers} />
      <button onClick={onNext} data-testid="objective-next" className="btn-primary w-full mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

const PROJ_MSGS = ["On analyse tes réponses…", "On calcule ton point de départ…", "On prépare ta projection…"];

function ProjectionLoading({ onNext }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    clarityEvent("chargement_projection");
    const step = setInterval(() => setI((v) => (v + 1) % PROJ_MSGS.length), 3200);
    const done = setTimeout(onNext, 10000);
    return () => { clearInterval(step); clearTimeout(done); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="proj-loading" data-testid="projection-loading">
      <p className="eyebrow">TA PROJECTION</p>
      <div className="proj-bubble" aria-hidden="true"><span className="proj-bubble-fill" /></div>
      <div className="proj-msg" role="status" aria-live="polite">
        <AnimatePresence mode="wait">
          <motion.span key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.4 }} data-testid="projection-loading-msg">{PROJ_MSGS[i]}</motion.span>
        </AnimatePresence>
      </div>
      <div className="proj-bar" aria-hidden="true"><span className="proj-bar-fill" /></div>
    </div>
  );
}

function projClientsRange(answers) {
  const t = String(answers.q10 || "");
  if (t.includes("Moins de 5")) return [5, 12];       // < 1 h/jour
  if (t.includes("20h")) return [20, 40];             // > 3 h/jour
  if (t.includes("5-10") || t.includes("10-20")) return [10, 25]; // 1 à 3 h/jour
  return [10, 25];
}

function projAvgPrice(answers) {
  const b = String(answers.q19 || "");
  const physical = String(answers.q6 || "").toLowerCase().includes("phys");
  let p = 30;
  if (b.includes("100")) p = 50;
  else if (b.includes("50")) p = 40;
  if (physical) p = Math.round(p * 1.5);
  return p;
}

const projEuro = (v) => `${Math.round(v).toLocaleString("fr-FR")} €`;

function ProjLever({ dot, title, range, body, open, onToggle, testid }) {
  return (
    <div className={`proj-acc-row ${open ? "open" : ""}`} data-testid={testid}>
      <button type="button" className="proj-acc-head" onClick={onToggle} aria-expanded={open}>
        <span className={`proj-acc-dot ${dot}`} aria-hidden="true" />
        <span className="proj-acc-title">{title}</span>
        <span className="proj-acc-range mono">{range}</span>
        <span className="proj-acc-chevron" aria-hidden="true">⌄</span>
      </button>
      <div className="proj-acc-body"><p>{body}</p></div>
    </div>
  );
}

const RESULT_REVIEWS = [
  { name: "Marine", stars: 5, text: "J'ai fait mes premiers 2 000 € en 6 semaines. Je n'y croyais pas au début." },
  { name: "Thomas", stars: 5, text: "3 400 € le deuxième mois grâce au plan jour par jour, tout était clair." },
  { name: "Sofia", stars: 5, text: "Mes 5 premiers clients en 3 semaines, environ 1 800 € encaissés." },
  { name: "Karim", stars: 5, text: "Parti de zéro, aujourd'hui je dépasse 2 500 € par mois." },
  { name: "Léa", stars: 4, text: "Mes premiers 1 200 € le premier mois, juste avec mon téléphone." },
  { name: "Hugo", stars: 5, text: "Le coach IA m'a débloqué : +3 000 € en deux mois." },
  { name: "Inès", stars: 5, text: "Première vente au bout de 4 jours. Aujourd'hui je vise les 3 000 €." },
];

function ResultReviewCard({ r }) {
  return (
    <div className="result-review-card">
      <div className="result-review-stars" aria-hidden="true">{"★".repeat(r.stars)}</div>
      <p className="result-review-text">« {r.text} »</p>
      <p className="result-review-name mono">— {r.name}</p>
    </div>
  );
}

function SocialProofScreen({ onNext }) {
  useEffect(() => { clarityEvent("ecran_social_preuve"); }, []);
  const loop = [...RESULT_REVIEWS, ...RESULT_REVIEWS];
  return (
    <div className="socialproof-screen" data-testid="socialproof-screen">
      <p className="eyebrow">TU N'ES PAS SEUL·E</p>
      <div className="socialproof-count" data-testid="socialproof-count">
        +<Counter to={50000} duration={1.5} format={(v) => v.toLocaleString("fr-FR")} />
      </div>
      <h1 className="quiz-question socialproof-title">utilisateurs nous font confiance.</h1>
      <p className="muted socialproof-sub">Ils ont créé leur business grâce à nous et ils ont généré des revenus.</p>

      <div className="result-marquee" data-testid="result-reviews">
        <div className="result-marquee-track">
          {loop.map((r, i) => <ResultReviewCard key={i} r={r} />)}
        </div>
      </div>

      <button onClick={onNext} data-testid="socialproof-next" className="btn-primary w-full mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function ProjectionScreen({ answers, onNext }) {
  const [open, setOpen] = useState(null);
  const goal = Math.max(300, Number(answers.q21 || 1500));
  const price = projAvgPrice(answers);
  const r10 = (v) => Math.round(v / 10) * 10;
  // Scénario annuel extrapolé à partir de l'objectif : montée en puissance prudente sur 12 mois.
  const min1 = r10(goal * 0.85), max1 = r10(goal * 1.7);          // ventes directes
  const min2 = r10(min1 * 0.10), max2 = r10(max1 * 0.20);          // bouche-à-oreille
  const min3 = r10(min1 * 0.10), max3 = r10(max1 * 0.30);          // réseaux sociaux
  const min = min1 + min2 + min3, max = max1 + max2 + max3;        // total sur un an
  const N = Math.max(1, Math.round(max1 / price / 12));            // clients/mois moyens
  const segTotal = max1 + max2 + max3 || 1;
  const w = [max1, max2, max3].map((v) => `${(v / segTotal) * 100}%`);
  useEffect(() => { clarityEvent("ecran_projection"); }, []);
  const toggle = (i) => setOpen((o) => (o === i ? null : i));
  return (
    <div className="proj-screen" data-testid="projection-screen">
      <p className="eyebrow">TA PROJECTION · SCÉNARIO, PAS UNE PROMESSE</p>

      <div className="proj-card">
        <p className="proj-card-label">CE QUE TON BUSINESS POURRAIT GÉNÉRER SUR UN AN</p>
        <div className="proj-amount" data-testid="projection-amount">
          <Counter to={min} duration={1.5} format={projEuro} /> <span className="proj-amount-to">à</span> <Counter to={max} duration={1.5} format={projEuro} />
        </div>
        <div className="proj-levers-bar" aria-hidden="true">
          <span className="proj-lever-seg seg-violet" style={{ width: w[0], animationDelay: "0.2s" }} />
          <span className="proj-lever-seg seg-blue" style={{ width: w[1], animationDelay: "0.7s" }} />
          <span className="proj-lever-seg seg-pink" style={{ width: w[2], animationDelay: "1.2s" }} />
        </div>
        <p className="proj-subline">Environ {N} clients par mois à {price} € en moyenne, plus 2 leviers de croissance · ton objectif : {goal.toLocaleString("fr-FR")} €</p>
        <p className="proj-explain">On part de ton objectif de {goal.toLocaleString("fr-FR")} € et on estime, prudemment, une montée en puissance sur 12 mois : tu n'atteins qu'une partie de ton objectif au début, puis ça grimpe grâce au bouche-à-oreille et à tes réseaux.</p>
      </div>

      <div className="proj-accordion" data-testid="projection-levers">
        <ProjLever testid="lever-ventes" dot="seg-violet" title="Tes ventes directes" range={`${projEuro(min1)} – ${projEuro(max1)}`} body="Les clients que tu trouves toi-même grâce à ton plan et tes messages de prospection." open={open === 0} onToggle={() => toggle(0)} />
        <ProjLever testid="lever-bouche" dot="seg-blue" title="Le bouche-à-oreille" range={`${projEuro(min2)} – ${projEuro(max2)}`} body="Tes premiers clients satisfaits qui te recommandent autour d'eux." open={open === 1} onToggle={() => toggle(1)} />
        <ProjLever testid="lever-reseaux" dot="seg-pink" title="Tes réseaux sociaux" range={`${projEuro(min3)} – ${projEuro(max3)}`} body="Les clients qui arrivent grâce aux posts prêts à publier de ton plan." open={open === 2} onToggle={() => toggle(2)} />
      </div>

      <p className="proj-note muted text-xs">C'est un scénario, pas une promesse. Ces chiffres sont extrapolés à partir de tes réponses et supposent que tu appliques ton plan avec régularité. Tes résultats réels dépendent de ton implication.</p>
      <button onClick={onNext} data-testid="projection-next" className="btn-primary w-full mt-6">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function DrawIcon({ name }) {
  const common = { fill: "none", stroke: "url(#introGrad)", strokeWidth: 5, strokeLinecap: "round", strokeLinejoin: "round", className: "intro-draw" };
  return (
    <svg viewBox="0 0 120 120" className="intro-icon-svg" aria-hidden="true">
      <defs>
        <linearGradient id="introGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="50%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
      </defs>
      {name === "sparkle" && (
        <path {...common} d="M60 18 C64 44 76 56 102 60 C76 64 64 76 60 102 C56 76 44 64 18 60 C44 56 56 44 60 18 Z" />
      )}
      {name === "bulb" && (
        <>
          <path {...common} d="M60 20 C42 20 30 33 30 50 C30 62 37 69 43 76 C47 80 48 85 48 90 L72 90 C72 85 73 80 77 76 C83 69 90 62 90 50 C90 33 78 20 60 20 Z" />
          <path {...common} d="M50 100 L70 100 M54 108 L66 108" />
        </>
      )}
      {name === "phone" && (
        <>
          <rect {...common} x="38" y="16" width="44" height="88" rx="10" />
          <path {...common} d="M54 26 L66 26" />
          <circle {...common} cx="60" cy="92" r="3" />
        </>
      )}
      {name === "podium" && (
        <>
          <rect {...common} x="48" y="46" width="24" height="58" rx="3" />
          <rect {...common} x="20" y="66" width="24" height="38" rx="3" />
          <rect {...common} x="76" y="76" width="24" height="28" rx="3" />
          <path {...common} d="M60 22 l5 11 12 1 -9 8 3 12 -11 -6 -11 6 3 -12 -9 -8 12 -1 Z" />
        </>
      )}
    </svg>
  );
}

function IntroScreen({ data, onNext }) {
  useEffect(() => { clarityEvent(data.event); }, [data.event]);
  return (
    <div className="intro-screen" data-testid={`intro-screen-${data.event}`}>
      <span className="intro-icon" aria-hidden="true"><DrawIcon name={data.icon} /></span>
      <div className="intro-text-reveal">
        <p className="eyebrow intro-label">{data.label}</p>
        <h1 className="quiz-question intro-title">{data.title}</h1>
        <p className="intro-body muted">{data.text}</p>
        <button onClick={onNext} data-testid={`${data.event}-next`} className="btn-primary mt-8">{data.cta} <span aria-hidden="true">→</span></button>
      </div>
    </div>
  );
}

function CapScreen({ answers, onNext }) {
  const goal = Number(answers.q21 || 0);
  const ambitious = goal > 2000;
  useEffect(() => { clarityEvent("ecran_cap"); }, []);
  return (
    <div className="cap-screen interstitial" data-testid="cap-screen">
      <p className="eyebrow">TON CAP</p>
      <h1 className="quiz-question">{ambitious ? "C'est ambitieux, et c'est très bien." : "C'est un objectif atteignable."}</h1>
      <p className="muted max-w-[460px]">{ambitious ? "On commence par ton premier palier, puis on monte." : "Et on va le découper en étapes avec toi."}</p>
      <button onClick={onNext} data-testid="cap-next" className="btn-primary mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function TypeBusinessScreen({ answers, setAnswers, onNext }) {
  useEffect(() => { clarityEvent("ecran_type_business"); }, []);
  const pick = (val) => { setAnswers((a) => ({ ...a, q6: val })); setTimeout(onNext, 350); };
  const cards = [
    { val: "En ligne", title: "Un business en ligne", desc: "Depuis ton téléphone, où tu veux, quand tu veux. Il faut du contenu pour trouver tes clients." },
    { val: "Physique", title: "Un business physique", desc: "Des clients près de chez toi, souvent plus rapides à trouver. Il faut te déplacer." },
  ];
  return (
    <div data-testid="typebiz-screen">
      <p className="eyebrow">TON PROJET</p>
      <h1 className="quiz-question">Tu préfères lancer quoi ?</h1>
      <div className="bigcards">
        {cards.map((c) => (
          <button key={c.val} onClick={() => pick(c.val)} data-testid={`typebiz-${c.val === "En ligne" ? "online" : "physical"}`} className={`bigcard ${answers.q6 === c.val ? "selected" : ""}`}>
            <span className="bigcard-title">{c.title}</span>
            <span className="bigcard-desc">{c.desc}</span>
          </button>
        ))}
      </div>
      <button onClick={() => pick("Les deux")} data-testid="typebiz-unsure" className="quiz-skip-link">Je ne sais pas, choisis pour moi</button>
    </div>
  );
}

function WordsScreen({ answers, setAnswers, onNext }) {
  const [v, setV] = useState(answers.user_words || "");
  useEffect(() => { clarityEvent("ecran_mots"); }, []);
  const save = (skip) => { setAnswers((a) => ({ ...a, user_words: skip ? "" : v.trim() })); onNext(); };
  return (
    <div data-testid="words-screen">
      <p className="eyebrow">TES MOTS</p>
      <h1 className="quiz-question">Décris en une phrase où tu veux être dans un an.</h1>
      <textarea
        value={v} onChange={(e) => setV(e.target.value)} rows={3}
        data-testid="words-input" data-clarity-mask="true"
        placeholder="Ex : avoir mes propres revenus et ne plus attendre la fin du mois."
        className="field words-field"
      />
      <button onClick={() => save(false)} disabled={!v.trim()} data-testid="words-validate" className="btn-primary w-full mt-6">Valider <span aria-hidden="true">→</span></button>
      <button onClick={() => save(true)} data-testid="words-skip" className="quiz-skip-link">Passer cette étape</button>
    </div>
  );
}

function PrenomScreen({ answers, setAnswers, onNext }) {
  const [v, setV] = useState(answers.prenom || "");
  useEffect(() => { clarityEvent("ecran_prenom"); }, []);
  const submit = (e) => { e.preventDefault(); if (!v.trim()) return; setAnswers((a) => ({ ...a, prenom: v.trim() })); onNext(); };
  return (
    <form onSubmit={submit} data-testid="prenom-screen">
      <p className="eyebrow">FAISONS CONNAISSANCE</p>
      <h1 className="quiz-question">Ton prénom ?</h1>
      <input value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid="prenom-input" placeholder="Ton prénom" className="field" />
      <button type="submit" disabled={!v.trim()} data-testid="prenom-next" className="btn-primary w-full mt-6">Continuer <span aria-hidden="true">→</span></button>
    </form>
  );
}

function PresqueFiniScreen({ prenom, onNext }) {
  useEffect(() => { clarityEvent("ecran_presque_fini"); }, []);
  return (
    <div className="interstitial" data-testid="presquefini-screen">
      <p className="eyebrow">PRESQUE FINI</p>
      <h1 className="quiz-question">{prenom ? `${prenom}, ` : ""}encore quelques questions rapides pour finaliser ton plan.</h1>
      <button onClick={onNext} data-testid="presquefini-next" className="btn-primary mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function CommentScreen({ prenom, onNext }) {
  const steps = [
    { n: "01", t: "L'IA choisit ton business et écrit ton plan jour par jour" },
    { n: "02", t: "Chaque matin, tu reçois ta mission du jour" },
    { n: "03", t: "Ton coach IA te guide jusqu'à ton premier client" },
  ];
  useEffect(() => { clarityEvent("ecran_comment"); }, []);
  return (
    <div data-testid="comment-screen">
      <p className="eyebrow">COMMENT ON T'Y AMÈNE</p>
      <h1 className="quiz-question">{prenom ? `${prenom}, ` : ""}voilà comment on t'amène à tes premiers euros.</h1>
      <div className="how-steps">
        {steps.map((s, i) => (
          <div key={s.n} className="how-step" style={{ animationDelay: `${0.3 + i * 0.5}s` }}>
            <span className="how-step-num mono">{s.n}</span>
            <span className="how-step-text">{s.t}</span>
          </div>
        ))}
      </div>
      <button onClick={onNext} data-testid="comment-next" className="btn-primary w-full mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function DebloquesScreen({ onNext }) {
  const items = ["Ton business sur-mesure", "Ton plan jour par jour", "Le script de ton premier client", "Ton coach IA", "Tes posts et messages de prospection prêts à envoyer"];
  useEffect(() => { clarityEvent("ecran_debloques"); }, []);
  return (
    <div data-testid="debloques-screen">
      <p className="eyebrow">CE QUE TU DÉBLOQUES</p>
      <h1 className="quiz-question">Tout ça t'attend.</h1>
      <ul className="unlock-list">
        {items.map((t, i) => (
          <li key={t} className="unlock-item" style={{ animationDelay: `${0.2 + i * 0.25}s` }}>
            <span className="unlock-check" aria-hidden="true"><Check size={14} /></span>{t}
          </li>
        ))}
      </ul>
      <button onClick={onNext} data-testid="debloques-next" className="btn-primary w-full mt-8">Continuer <span aria-hidden="true">→</span></button>
    </div>
  );
}

function GarantieScreen({ onNext }) {
  useEffect(() => { clarityEvent("ecran_garantie"); }, []);
  return (
    <div className="interstitial" data-testid="garantie-screen">
      <span className="garantie-shield" aria-hidden="true"><ShieldCheck size={40} /></span>
      <p className="eyebrow">AVANT D'ALLER PLUS LOIN</p>
      <h1 className="quiz-question">On prend le risque à ta place.</h1>
      <p className="muted max-w-[460px]">Applique les 2 premiers jours de ton plan. Pas convaincu·e ? On te rembourse.</p>
      <button onClick={onNext} data-testid="garantie-next" className="btn-primary mt-8">J'ai compris <span aria-hidden="true">→</span></button>
    </div>
  );
}

function PacteScreen({ onDone }) {
  const [holding, setHolding] = useState(false);
  const [done, setDone] = useState(false);
  const start = (e) => { e.preventDefault(); if (!done) setHolding(true); };
  const stop = () => { if (!done) setHolding(false); };
  const onFilled = () => {
    if (done) return;
    setDone(true);
    setHolding(false);
    clarityEvent("pacte_valide");
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(35);
    setTimeout(onDone, 850);
  };
  return (
    <div className="pacte" data-testid="pacte-screen">
      <p className="eyebrow">TON ENGAGEMENT</p>
      <h2>Avant de découvrir ton business, <em>engage-toi.</em></h2>
      <p className="muted mt-4 max-w-[460px]">Je m'engage à suivre mon plan jusqu'au bout, même quand ce sera dur. Je ne lâche pas au premier obstacle.</p>
      <button
        type="button"
        data-testid="pacte-hold"
        className={`pacte-hold ${holding ? "holding" : ""} ${done ? "done" : ""}`}
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span className="pacte-ring" aria-hidden="true">
          <svg viewBox="0 0 120 120">
            <defs>
              <linearGradient id="pacteGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="50%" stopColor="#6366F1" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
            <circle className="pacte-ring-bg" cx="60" cy="60" r="54" />
            <circle className="pacte-ring-fill" cx="60" cy="60" r="54" onAnimationEnd={onFilled} />
          </svg>
          <span className="pacte-fingerprint" aria-hidden="true"><Fingerprint size={34} /></span>
        </span>
        <span className="pacte-hold-label">
          {done ? <><Check size={18} /> Signé</> : "RESTE APPUYÉ POUR SIGNER"}
        </span>
      </button>
      <p className="muted text-xs mt-6">Maintiens le cercle 3 secondes pour signer ton engagement.</p>
    </div>
  );
}

function LoadingScreen({ answers, navigate }) {
  const reduce = useReducedMotion();
  const [progress, setProgress] = useState(0); // 0..1, single source of truth
  const [finalizing, setFinalizing] = useState(false);
  const posted = useRef(false);
  const startTime = useRef(null);
  const lastPoll = useRef(0);
  const genDone = useRef(false);
  const genFailed = useRef(false);
  const resultId = useRef(null);
  const navigated = useRef(false);

  useEffect(() => {
    if (!posted.current) {
      posted.current = true;
      trackWhop("submit_application");
      clarityEvent("generation_lancee");
      const apiAnswers = buildAnswersForApi(answers);
      if (answers.prenom) apiAnswers.prenom = answers.prenom;
      if (answers.user_words) apiAnswers.user_words = answers.user_words;
      api.post("/generate", { session_id: getSessionId(), answers: apiAnswers })
        .then(({ data }) => { resultId.current = data.result_id; })
        .catch(() => { genFailed.current = true; });
    }

    let raf;
    const frame = (now) => {
      if (startTime.current === null) startTime.current = now;
      const el = now - startTime.current;
      const t = Math.min(el / DURATION, 1);
      const p = curve(t);

      // Throttled generation poll driven from the same loop (no separate timers).
      if (resultId.current && now - lastPoll.current > 2000) {
        lastPoll.current = now;
        api.get(`/result/${resultId.current}`)
          .then(({ data }) => { if (data.generated) genDone.current = true; })
          .catch(() => {});
      }

      const timeUp = el >= DURATION;

      if (timeUp && resultId.current && !navigated.current) {
        navigated.current = true;
        setProgress(1);
        setFinalizing(false);
        clearAnswers();
        sessionStorage.removeItem("idealab_qidx");
        setTimeout(() => navigate(`/result/${resultId.current}`), 400);
        return;
      }
      if (timeUp && genFailed.current && !resultId.current && !navigated.current) {
        navigated.current = true;
        navigate("/quiz");
        return;
      }

      setProgress(timeUp
        ? Math.min(0.94 + (0.99 - 0.94) * (1 - Math.exp(-(el - DURATION) / FINAL_TAU)), 0.99)
        : Math.min(p, 0.94));
      setFinalizing(timeUp && !genDone.current);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = Math.floor(progress * 100);
  const active = LOAD_STEPS.reduce((acc, s, i) => (progress >= s.t ? i : acc), 0);
  const message = finalizing ? "Finalisation de ton plan" : LOAD_STEPS[active].label;
  const offset = CIRC * (1 - progress);

  return (
    <div className="load18" data-testid="generation-screen">
      <p className="eyebrow">02 — DANS LE LABO</p>

      <motion.div className="load-ring" key={reduce ? "ring" : active} animate={reduce ? {} : { scale: [1, 1.03, 1] }} transition={{ duration: 0.25 }}>
        {!reduce && <div className="load-ring-halo" aria-hidden="true" />}
        <svg viewBox="0 0 200 200" aria-hidden="true">
          <defs>
            <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="50%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="var(--line)" strokeWidth="6" />
          <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="url(#ringGrad)" strokeWidth="6" strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={offset} />
        </svg>
        <div className="load-ring-pct" data-testid="generation-percent">{pct} %</div>
      </motion.div>

      <div className="load-msg2">
        <AnimatePresence mode="wait">
          <motion.span
            key={finalizing ? "final" : active}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8, filter: "blur(6px)" }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, filter: "blur(6px)" }}
            transition={{ duration: 0.4 }}
            role="status" aria-live="polite" data-testid="generation-message"
          >
            {message}
          </motion.span>
        </AnimatePresence>
      </div>

      <div className="load-bar2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid="generation-progress">
        <div className="load-bar2-fill" style={{ width: `${progress * 100}%` }} />
      </div>

      <div className="load-steps2">
        {LOAD_STEPS.map((s, i) => {
          const isDone = i < active || (progress >= 1 && i === LOAD_STEPS.length - 1);
          const isCurrent = i === active && !isDone;
          return (
            <motion.div
              key={i}
              className={`load-step2 ${isDone ? "done" : ""} ${isCurrent ? "current" : ""}`}
              data-testid={`load-step-${i}`}
              initial={reduce ? { opacity: 1 } : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: reduce ? 0 : i * 0.06 }}
            >
              <span className="load-step2-box">
                {isDone
                  ? <motion.span initial={reduce ? false : { scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}><Check size={13} /></motion.span>
                  : isCurrent ? <span className="load-step2-dot" /> : null}
              </span>
              <span>{s.label}</span>
            </motion.div>
          );
        })}
      </div>

      <p className="load40-warn">Ne ferme pas cette page.</p>
      <ReviewTicker variant="center" />
    </div>
  );
}
